/**
 * The fetch + validate core for remote pack subscriptions (Phase 4.2). Pure and
 * dependency-injectable (pass `fetchImpl`), so it can be unit-tested without a
 * network. No store, no UI here.
 *
 * Detection strategy follows the verified CORS constraints: a plain `GET` (no
 * custom headers → no preflight) reads the body, and `contentHash` (sha-256 of
 * the bytes) is the authoritative "is this different?" signal because most hosts
 * don't expose `ETag` cross-origin. Nothing is trusted until it passes
 * {@link validateRawPack}.
 */
import type { RawContentPack } from '../types/content'
import type { PackCode, PackPointer, PackSourceKind, Revision, SyncError } from '../types/subscription'
import { validateRawPack } from './validatePack'

/** Reject bodies larger than this before parsing, to bound work on a hostile response. */
export const DEFAULT_MAX_BYTES = 2 * 1024 * 1024

const PACK_CODE_PREFIX = 'charsmith:pack:'

const encoder = new TextEncoder()

// --- pack codes (encoded pointers; never content) ----------------------------

function toBase64Url(s: string): string {
  let bin = ''
  for (const b of encoder.encode(s)) bin += String.fromCharCode(b)
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function fromBase64Url(b64url: string): string {
  const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/')
  const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0))
  return new TextDecoder().decode(bytes)
}

/** A display/warning hint only — the fetch path treats every source identically. */
export function inferSourceKind(url: string): PackSourceKind {
  let host = ''
  try {
    host = new URL(url).hostname
  } catch {
    return 'https'
  }
  if (host === 'gist.githubusercontent.com' || host === 'gist.github.com') return 'github-gist'
  if (host === 'raw.githubusercontent.com' || host === 'github.com') return 'github-repo'
  if (host === 'cdn.jsdelivr.net') return 'jsdelivr'
  return 'https'
}

/** Encode a pointer as a shareable `charsmith:pack:<base64url>` token (no content). */
export function encodePackCode(pointer: PackPointer): string {
  const code: PackCode = { v: 1, pointer }
  return PACK_CODE_PREFIX + toBase64Url(JSON.stringify(code))
}

/**
 * Resolve a player's pasted input into a {@link PackPointer}. Accepts either a
 * `charsmith:pack:` code or a bare HTTPS URL (in which case `packId` is left blank
 * and gets filled from the pack's own id on first fetch). Returns `null` for input
 * we can't make sense of.
 */
export function decodePackCode(input: string): PackPointer | null {
  const trimmed = input.trim()
  if (trimmed.startsWith(PACK_CODE_PREFIX)) {
    try {
      const parsed: unknown = JSON.parse(fromBase64Url(trimmed.slice(PACK_CODE_PREFIX.length)))
      if (!parsed || typeof parsed !== 'object') return null
      const code = parsed as Partial<PackCode>
      const p = code.pointer
      if (!p || typeof p.url !== 'string' || typeof p.packId !== 'string') return null
      return p
    } catch {
      return null
    }
  }
  // Bare URL: only HTTPS is fetchable cross-origin; packId resolves on first fetch.
  try {
    const url = new URL(trimmed)
    if (url.protocol !== 'https:') return null
    return { packId: '', url: trimmed, kind: inferSourceKind(trimmed) }
  } catch {
    return null
  }
}

// --- content hashing ---------------------------------------------------------

/** Lowercase hex sha-256 of `text` — the primary revision identity. */
export async function hashContent(text: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', encoder.encode(text))
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

// --- fetch + validate --------------------------------------------------------

export type FetchPackResult =
  | { ok: true; pack: RawContentPack; revision: Revision }
  | { ok: false; error: SyncError }

interface FetchPackOptions {
  /** Injected for testing; defaults to the global `fetch`. */
  fetchImpl?: typeof fetch
  maxBytes?: number
}

const fail = (kind: SyncError['kind'], message: string, httpStatus?: number): FetchPackResult => ({
  ok: false,
  error: { kind, message, httpStatus, at: new Date().toISOString() },
})

/**
 * Fetch a pack from its pointer and return validated content + a fresh
 * {@link Revision}, or a typed {@link SyncError}. A plain `GET` (no custom headers)
 * keeps it a CORS "simple request" so it works wherever the host allows browser
 * access at all. Order: size cap → parse → schema-validate → hash.
 */
export async function fetchPack(
  pointer: PackPointer,
  { fetchImpl = fetch, maxBytes = DEFAULT_MAX_BYTES }: FetchPackOptions = {},
): Promise<FetchPackResult> {
  let res: Response
  try {
    res = await fetchImpl(pointer.url, { method: 'GET', redirect: 'follow' })
  } catch {
    // Browsers throw the same opaque TypeError for network failure and CORS
    // rejection and hide the status, so we can't honestly tell them apart.
    return fail('unreachable', "Couldn't reach this URL. It may be offline, moved, or block browser access.")
  }

  if (!res.ok) return fail('http', `Server returned ${res.status} ${res.statusText}.`, res.status)

  // Cheap early reject when the host advertises a size (CORS may hide it).
  const declaredLen = Number(res.headers.get('content-length'))
  if (declaredLen > maxBytes) return fail('too-large', sizeMessage(declaredLen, maxBytes))

  const text = await res.text()
  const bytes = encoder.encode(text).length
  if (bytes > maxBytes) return fail('too-large', sizeMessage(bytes, maxBytes))

  let data: unknown
  try {
    data = JSON.parse(text)
  } catch {
    return fail('parse', 'Response is not valid JSON.')
  }

  const problems = validateRawPack(data)
  if (problems.length) return fail('schema', `Pack failed validation: ${problems.join(' ')}`)

  const pack = data as RawContentPack
  const revision: Revision = {
    contentHash: await hashContent(text),
    etag: res.headers.get('etag') ?? undefined,
    lastModified: res.headers.get('last-modified') ?? undefined,
    declaredVersion: pack.version,
    fetchedAt: new Date().toISOString(),
  }
  return { ok: true, pack, revision }
}

const MB = 1024 * 1024
const sizeMessage = (bytes: number, max: number) =>
  `Pack is ${(bytes / MB).toFixed(1)} MB; the limit is ${Math.round(max / MB)} MB.`
